import React, { useState } from 'react';
import { Link, Button, Field, Input, Table, Icon, Stack, Text, Box } from '@chakra-ui/react';
import { CheckCircleIcon, XCircleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { toaster } from '@/components/ui/toaster';
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import api from '@/utils/api';
import { DailogProps, User } from '@/lib/types';

interface Props extends DailogProps {
  onSuccess(): void;
}

export default function ImportUsersModal({ isOpen, onClose, onSuccess }: Props) {
  const [response, setResponse] = useState<any>(null);
  const [file, setFile] = useState<any>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setFile(file);
    }
  };

  async function onSubmit() {
    try {
      if (!file) {
        toaster.create({
          type: 'error',
          title: 'Please select a file',
        });
        return;
      }
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`users/invite-csv`, formData);
      setResponse(response.data.resource || []);
      toaster.create({
        type: 'success',
        title: 'Imported users successfully',
      });
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update user',
      });
    }
  }

  return (
    <DialogRoot lazyMount open={isOpen} onOpenChange={onClose}>
      <DialogBackdrop />
      <DialogTrigger />
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <DialogTitle>Invite User</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <>
            {response && response.length > 0 ? (
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Name</Table.ColumnHeader>
                    <Table.ColumnHeader>Email</Table.ColumnHeader>
                    <Table.ColumnHeader>Invitation Sent</Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {response.map((user: User) => (
                    <Table.Row key={user.email}>
                      <Table.Cell>
                        {user.first_name} {user.last_name}
                      </Table.Cell>
                      <Table.Cell>{user.email}</Table.Cell>
                      <Table.Cell>
                        {user.is_invitation_sent ? (
                          <Icon as={CheckCircleIcon} color={'green.500'} />
                        ) : (
                          <Icon as={XCircleIcon} color={'red.500'} />
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            ) : (
              <form id={'inviteUser'}>
                <Stack gap={6}>
                  <Box borderWidth={'1px'} borderRadius={'lg'} p={4}>
                    <Text fontWeight={'bold'} mb={2}>
                      Only CSV files are allowed
                    </Text>
                    <Link variant='underline' href='/example.csv' download>
                      <Icon as={ArrowDownTrayIcon} w={4} h={4} />
                      Download Example File
                    </Link>
                  </Box>
                  <Box borderWidth={'1px'} borderRadius={'lg'} p={4}>
                    <Field.Root>
                      <Input
                        border={'none'}
                        type='file'
                        accept='.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
                        onChange={handleFileChange}
                      />
                    </Field.Root>
                  </Box>
                </Stack>
              </form>
            )}
          </>
        </DialogBody>
        <DialogFooter>
          <Button colorPalette='brand' variant={'outline'} onClick={onSuccess}>
            Cancel
          </Button>
          {!response && (
            <Button colorPalette='brand' type={'submit'} onClick={onSubmit}>
              Submit
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
